import { SimpleGrid, Text, RingProgress, Progress, Skeleton, Tooltip, Badge, Box, Stack } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { dashboardApi } from '@/api/dashboard'
import type { CapacityMetric } from '@/api/dashboard'
import { WidgetCard, CardHeader, Callout } from '@/components/ui'

interface CapacityForecastProps {
  dbIdentifier: string
}

function severityColor(pct: number | null): string {
  if (pct === null) return 'gray'
  if (pct >= 80) return 'red'
  if (pct >= 60) return 'yellow'
  return 'teal'
}

function forecastLabel(metric: CapacityMetric): string {
  if (metric.current_pct === null) return '—'
  if (metric.days_until_threshold !== null && metric.days_until_threshold > 0) {
    return `Threshold em ${metric.days_until_threshold} dias`
  }
  if (metric.growth_rate_per_day !== null && metric.growth_rate_per_day <= 0) {
    return 'Estável'
  }
  return '—'
}

interface MetricColumnProps {
  label: string
  metric: CapacityMetric
}

function MetricColumn({ label, metric }: MetricColumnProps) {
  const pct = metric.current_pct ?? 0
  const color = severityColor(metric.current_pct)

  return (
    <Stack align="center" gap="xs">
      <Tooltip label={`Crescimento: ${metric.growth_rate_per_day ?? 0}%/dia`}>
        <RingProgress
          size={100}
          thickness={10}
          roundCaps
          sections={[{ value: pct, color }]}
          label={
            <Text ta="center" fw={700} size="sm">
              {metric.current_pct !== null ? `${Math.round(pct)}%` : '—'}
            </Text>
          }
        />
      </Tooltip>
      <Text fw={500} size="sm">{label}</Text>
      <Progress value={pct} color={color} size="sm" w="100%" />
      <Text size="xs" c="dimmed">{forecastLabel(metric)}</Text>
    </Stack>
  )
}

export function CapacityForecast({ dbIdentifier }: CapacityForecastProps) {
  const tenant = useCurrentTenant()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['capacity-forecast', tenant?.id, dbIdentifier],
    queryFn: () => dashboardApi.getCapacityForecast(tenant!.id, dbIdentifier),
    enabled: !!tenant?.id,
    staleTime: 300_000,
  })

  if (isLoading) {
    return (
      <WidgetCard accent="info" p={0}>
        <CardHeader title="Previsão de Capacidade RDS" badge={{ value: dbIdentifier, color: 'blue' }} />
        <Box p="md">
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            {[0, 1, 2].map(i => (
              <Stack key={i} align="center" gap="xs">
                <Skeleton height={100} circle />
                <Skeleton height={14} width={80} />
                <Skeleton height={8} width="100%" />
                <Skeleton height={12} width={120} />
              </Stack>
            ))}
          </SimpleGrid>
        </Box>
      </WidgetCard>
    )
  }

  if (isError) {
    return (
      <WidgetCard accent="info" p={0}>
        <CardHeader title="Previsão de Capacidade RDS" badge={{ value: dbIdentifier, color: 'blue' }} />
        <Box p="md">
          <Callout variant="warning">Dados de previsão indisponíveis</Callout>
        </Box>
      </WidgetCard>
    )
  }

  if (
    data &&
    data.storage.current_pct === null &&
    data.connections.current_pct === null &&
    data.cpu.current_pct === null
  ) {
    return (
      <WidgetCard accent="info" p={0}>
        <CardHeader title="Previsão de Capacidade RDS" badge={{ value: dbIdentifier, color: 'blue' }} />
        <Box p="md">
          <Callout variant="info">Nenhum snapshot coletado ainda</Callout>
        </Box>
      </WidgetCard>
    )
  }

  return (
    <WidgetCard accent="info" p={0}>
      <CardHeader title="Previsão de Capacidade RDS" badge={{ value: dbIdentifier, color: 'blue' }} />
      <Box p="md">
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <MetricColumn label="Armazenamento" metric={data!.storage} />
          <MetricColumn label="Conexões" metric={data!.connections} />
          <MetricColumn label="CPU" metric={data!.cpu} />
        </SimpleGrid>
      </Box>
    </WidgetCard>
  )
}
